<template>
    <div class ="pricing-breakdown-container w-full" v-if="selectedPaymentType && pageLoaded">
        <div class="pricing-breakdown-header flex justify-between">
            <h5 class="pricing-breakdown-title lowercase capitalize">{{ renewalsPersistedVariables.jsonContent.text.products }}</h5>
            <div class="flex pt-2.5">
                <button class="flex gap-1/4 justify-between w-30" v-if="isPhoenixCustomer" @click="$emit('adjustQuantities')">
                    <img :src="checkoutSharedContent.renewalEditIcon.fields.file.url" />
                    <div class="edit-fields">{{ renewalsPersistedVariables.jsonContent.buttons.editQuantities }}</div>
                </button>
            </div>
        </div>
        <v-table
            v-if="softwareCart.length > 0"
            key="table1"
            :columns="buildHeaders('software')"
            :rows="buildRows('software')"
            :tableClass="'col xs-12 padding-top'">
        </v-table>
        <v-table
            v-if="hardwareCart.length > 0"
            key="table2"
            :columns="buildHeaders('hardware')"
            :rows="buildRows('hardware')"
            :tableClass="'col xs-12 padding-top-2'">
        </v-table>
        <breakdown-table
            :rows="pricingBreakdown"
            :tableClass="'col xs-12 sm-12 md-offset-4 md-8 lg-offset-6 lg-6 padding-top-2 no-side-padding'">
        </breakdown-table>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import VTable from './v-table';
import BreakdownTable from './breakdown-table';
import GeneratePricingBreakdown from '../../../mixins/generate-pricing-breakdown';
import {annualTermLength, standardLicenseTermLength} from '../../../settings';
import { SFDCPaymentTypeToVariableName } from '../../../../corpweb_globals'
import t from 'vue-slide-up-down';

export default {

    mixins: [GeneratePricingBreakdown],

    components: {VTable, BreakdownTable},
    props:{
        pageLoaded: {
            type: Boolean,
            required: true
        },
    },
    data() {
        return {
            discountFormula: {
                'monthly': {
                    'discount': 0,
                    'term': 1
                },
                'annual': {
                    'discount': 0,
                    'term': 12
                },
                'upfront': {
                    'discount': 0,
                    'term': 1
                }
            },
            paymentOptions: ['monthly', 'annual', 'upfront'],
        }
    },

    computed: {
        ...mapGetters([
            'currencySymbol',
            'isPhoenixCustomer',
            'isPhoenixPhaseTwoCustomer',
            'isPhoenixPhaseThreeCustomer'
        ]),

        ...mapGetters('shoppingCart', [
            'hardwareCart',
            'softwareCart',
            'productCostsInfo',
            'cablesInCartCount',
            'renewalDiscountMatrix'
        ]),

        ...mapGetters('checkout', [
            'licenseTerm',
            'isSelfServiced',
            'initialPaymentTerm',
            'selectedPaymentType',
            'isENT',
            'isRenewal',
            'renewalTerm'
        ]),

        ...mapGetters('content', [
            'checkoutSharedContent',
            'checkoutContent',
            'renewalsPersistedVariables'
        ]),

        ...mapGetters('shipping',[
            'shipping'
        ]),

        hasNetTerms() {
            if (this.initialPaymentTerm && this.initialPaymentTerm !== 'Due in advance') {
                return this.initialPaymentTerm;
            }
            return false;
        },

        formattedLicenseTableHeader() {
            let licenseStr = this.checkoutSharedContent.pricingTableHeaders.license;
            return this.selectedPaymentType === 'Direct Annual' ? 'License Annual Price' : licenseStr.replace("license_term", this.licenseTerm);
        },

        paymentType() {
            return SFDCPaymentTypeToVariableName[this.selectedPaymentType];
        },

        pricingBreakdown() {
            return this.paymentTypeToBreakdownFields[this.paymentType].map(lineItemName => {
                let label = this[lineItemName].label();
                if (label.includes('License Subtotal')) {
                    label += ` (${this.licenseTerm} months)`
                } else if ((this.hasNetTerms && this.isENT) && label.includes("Payment")) {
                    label += ` (Due ${this.initialPaymentTerm})`
                }
                return {
                    label,
                    value: this.formatPrice(this[lineItemName].value()),
                    display: this[lineItemName].display(),
                }
            })
        },
    },
    methods: {
        buildHeaders(typeOfCart) {
            let headers = {
                'productName': typeOfCart === 'software' ? this.formattedLicenseTableHeader : this.checkoutSharedContent.pricingTableHeaders.hardware,
                'quantity': this.checkoutSharedContent.pricingTableHeaders.quantity,
                'unitPrice': this.checkoutContent.pricingBreakdownTerms[typeOfCart === 'software' ? `${this.paymentType}_unit_price` : 'upfront_unit_price'],
                'total': this.checkoutSharedContent.pricingTableHeaders.total
            }
            return headers;
        },

        buildRows(typeOfCart) {
            let cart = `${typeOfCart}Cart`;
            let rows = this[cart].map(product => {
                let unitPrice = this.calculateUnitPriceWithDiscount(product.unit_price, product.list_price, typeOfCart, product.discount, product.monthly_unit_price, product.renewal_uplift_price_matrix);
                return {
                    productName: product.name,
                    quantity: this.determineQuantity(typeOfCart, product),
                    unitPrice: this.formatPrice(unitPrice),
                    total: this.formatPrice(unitPrice * this.determineQuantity(typeOfCart, product))
                }
            });
            
            //emit prices back for Renewals
            if(this.isRenewal && typeOfCart === 'software'){
                let productMap = {}
                rows.forEach((key) => productMap[key.productName] = key)
                this.$emit('productPrices', productMap)
            }
            // add cable placeholder product row for webstore deals if needed
            if (typeOfCart === 'hardware' && this.isSelfServiced && this.cablesInCartCount > 0) {
                let cablePlaceholderRow = {
                    productName: 'Samsara Cables',
                    quantity: this.cablesInCartCount,
                    unitPrice: this.formatPrice(0),
                    total: this.formatPrice(0)
                }
                rows.push(cablePlaceholderRow);
            }
            return rows;
        },

        formatPrice(number) {
            return `${this.currencySymbol}${number.toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
        },

        calculateUnitPriceWithDiscount(unitPrice, listPrice, typeOfCart, discount, monthlyUnitPrice, renewalUpliftPriceMatrix) {
            if (typeOfCart === 'hardware') {
                return unitPrice * (1 - discount);
            } else if (monthlyUnitPrice && this.paymentType === 'Direct Monthly') {
                return monthlyUnitPrice
            } else {
                let monthlyPriceLicenseTermLength = this.licenseTerm;
                let annualPriceTermLength = this.discountFormula[this.paymentType].term;
                if (this.isSelfServiced) {
                    monthlyPriceLicenseTermLength = standardLicenseTermLength
                    annualPriceTermLength = Math.min(annualTermLength, this.licenseTerm)
                }
                let price = null;
                if (this.isPhoenixPhaseTwoCustomer) {
                    price = renewalUpliftPriceMatrix[this.licenseTerm][this.selectedPaymentType]['price']
                } else if (this.isPhoenixPhaseThreeCustomer) {
                    price = (1 - (this.renewalDiscountMatrix[this.selectedPaymentType] / 100)) * listPrice  
                } else {
                    price = unitPrice
                }
                let monthlyUnitPriceCalc = (price / monthlyPriceLicenseTermLength) * (1 - discount) * (1 - this.discountFormula[this.paymentType].discount);
                if (monthlyUnitPrice) {
                    monthlyUnitPriceCalc = monthlyUnitPrice
                }
                return monthlyUnitPriceCalc * (this.paymentType == 'annual' && monthlyPriceLicenseTermLength < 12 ? monthlyPriceLicenseTermLength : this.discountFormula[this.paymentType].term);
            }
        },

        determineQuantity(cart, product){
            return cart === 'software' && product.quantity !== product.adjusted_quantity ?  product.adjusted_quantity: product.quantity
        }

    },
    mounted() {
        this.discountFormula.annual.discount = this.productCostsInfo.discounts.annual;
        this.discountFormula.upfront.discount = this.productCostsInfo.discounts.upfront;
        this.discountFormula.upfront.term = this.licenseTerm;
    },

    watch: {
        licenseTerm: {
            deep: true,
            handler() {
                this.discountFormula.upfront.term = this.licenseTerm;

            }
        },
        softwareCart:{
            deep: true,
            handler() {
                this.buildRows('software')
            }
        }
    }

}
</script>

<style>

    .left {
        text-align: left;
    }

    .no-border {
        border-bottom: 0;
    }

    .bold {
        font-weight: bold;
    }

    .row-border {
        padding: 10px;
        border-style: solid;
        border-color: #f0f0f0;
        border-width: 0 0 1px 0;
    }

    .no-side-padding {
        padding-left: 0px;
        padding-right: 0px;
    }
    .pricing-breakdown-title {
        font-weight: 500;
        font-size: 24px;
        border-left: 0px solid;
        border-right: 0px solid;
        border-top: 0px solid;
        padding-top: 10px;
        line-height:0px;
    }

    .pricing-breakdown-header{
        border-bottom: 2px solid #f0f0f0;
        margin-bottom: 20px;
    }

    .pricing-breakdown-container{
        min-height: 550px;
        padding-left: 11px;
    }

    .edit-fields{
        font-size: 18px;
        line-height: 22px;
        font-weight: 500;
        color: #0C6EDE
    }

</style>